/**
 * ShareMoment builders — the privacy boundary for shareable cards.
 *
 * Every builder maps app data down to counts, percentages, and directional
 * language. Account/institution names, merchant names, and user-authored
 * titles NEVER pass through — that boundary is enforced here in code and is
 * not template-editable. Dollar amounts appear ONLY in the optional
 * `detailed` variant (rounded whole dollars), which the composer exposes as
 * an explicit toggle; 'private' — fully amount-free — is always the default.
 * Templates (admin-editable via the shareCardTemplates collection, defaults
 * in @shared) control copy and tone only; rendered strings are scrubbed as
 * defense-in-depth, including the stat value/unit.
 */

import type { InsightData, UserGoal } from '@shared/types';
import {
  DEFAULT_SHARE_CARD_TEMPLATES,
  renderShareTemplate,
  scrubAmounts,
  SHARE_TONE_ACCENTS,
  type ShareCardTemplateContent,
  type ShareCardTemplateId,
  type ShareCardTemplates,
} from '@shared/shareCards/shareCardTemplates';
import type {
  ShareMoment,
  ShareMomentDetail,
  ShareMomentKind,
  ShareStat,
  ShareVizSpec,
} from '@shared/shareCards/shareCardTypes';

const APP_LINK = 'enough.app';

/** Plain text + attribution. Deliberately BARE — no scheme, no www. A full
 *  https:// URL makes iMessage (and Slack, WhatsApp, …) unfurl a rich link
 *  preview, which buries the card image under a giant bubble; the wordmark is
 *  already burned into the artwork, so the text only has to name us
 *  (Justin, 2026-09-09 — reverses the earlier auto-link decision). */
const withAttribution = (text: string): string => `${text} | ${APP_LINK}`;

/** Rounded whole dollars for the detailed variant — never cents. */
const formatShareAmount = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

interface DetailedArgs {
  /** Extra variables for the detailed copy (always includes `amount`). */
  vars: Record<string, string | number>;
  stat?: ShareStat;
  viz?: ShareVizSpec;
}

interface BuildArgs {
  templateId: ShareCardTemplateId;
  kind: ShareMomentKind;
  vars: Record<string, string | number>;
  stat: ShareMoment['stat'];
  viz: ShareVizSpec;
  source: string;
  /** Omit when the moment has no sensible dollar figure — the card then has
   *  no detailed variant and the composer hides the amounts toggle. */
  detailed?: DetailedArgs;
}

/** Defense-in-depth: the stat travels to the raster unscrubbed otherwise. */
const scrubStat = (stat: ShareStat, mode: 'private' | 'detailed'): ShareStat => ({
  value: scrubAmounts(stat.value, mode),
  ...(stat.unit !== undefined ? { unit: scrubAmounts(stat.unit, mode) } : {}),
});

function buildDetail(content: ShareCardTemplateContent, args: BuildArgs): ShareMomentDetail | undefined {
  const d = args.detailed;
  if (!d || (!content.detailedKicker && !content.detailedShareText)) return undefined;
  const vars = { ...args.vars, ...d.vars };
  return {
    ...(content.detailedKicker
      ? { kicker: renderShareTemplate(content.detailedKicker, vars, 'detailed') }
      : {}),
    ...(content.detailedShareText
      ? { shareText: withAttribution(renderShareTemplate(content.detailedShareText, vars, 'detailed')) }
      : {}),
    ...(d.stat ? { stat: scrubStat(d.stat, 'detailed') } : {}),
    ...(d.viz ? { viz: d.viz } : {}),
  };
}

function buildMoment(templates: ShareCardTemplates, args: BuildArgs): ShareMoment | null {
  const content: ShareCardTemplateContent | undefined = templates[args.templateId];
  if (!content || !content.active) return null;
  const detailed = buildDetail(content, args);
  return {
    kind: args.kind,
    eyebrow: renderShareTemplate(content.eyebrow, args.vars),
    headline: renderShareTemplate(content.headline, args.vars),
    kicker: renderShareTemplate(content.kicker, args.vars),
    stat: scrubStat(args.stat, 'private'),
    accent: SHARE_TONE_ACCENTS[content.tone],
    viz: args.viz,
    shareText: withAttribution(renderShareTemplate(content.shareText, args.vars)),
    source: args.source,
    ...(detailed ? { detailed } : {}),
  };
}

// ── Insights ────────────────────────────────────────────────────────────────

interface SparkPayloadLike {
  current?: { value?: number };
  prior?: { value?: number };
  categoryName?: string;
}

interface StreakPayloadLike {
  filled?: boolean[];
}

interface RankPayloadLike {
  percent?: number;
}

interface StatPayloadLike {
  value?: number | string;
  label?: string;
  unit?: string;
}

const round = (n: number): number => Math.round(n);

/**
 * Build a share moment from an insight card. Returns null when the insight
 * doesn't carry enough non-sensitive signal to make a good card.
 */
export function momentFromInsight(
  insight: InsightData,
  templates: ShareCardTemplates = DEFAULT_SHARE_CARD_TEMPLATES
): ShareMoment | null {
  const payload = insight.vizPayload ?? {};

  switch (insight.templateType) {
    case 'streak': {
      const filled = (payload as StreakPayloadLike).filled ?? [];
      const count = filled.filter(Boolean).length;
      if (count < 2) return null;
      return buildMoment(templates, {
        templateId: 'insight_streak',
        kind: 'streak',
        vars: { count },
        stat: { value: String(count), unit: 'days' },
        viz: { type: 'streak', count },
        source: 'insight_streak',
      });
    }
    case 'stat-comparison': {
      const p = payload as SparkPayloadLike;
      const prior = p.prior?.value ?? 0;
      const current = p.current?.value ?? 0;
      if (!(prior > 0) || current < 0) return null;
      const pct = round(Math.abs((current - prior) / prior) * 100);
      if (pct < 1 || pct > 100) return null;
      const direction: 'down' | 'up' = current <= prior ? 'down' : 'up';
      if (direction === 'up') return null; // only share the wins
      const category = (p.categoryName ?? '').toLowerCase() || 'spending';
      const savedDollars = prior - current;
      return buildMoment(templates, {
        templateId: 'insight_spend_comparison',
        kind: 'spending-shift',
        vars: { category, percent: pct },
        stat: { value: `−${pct}`, unit: '%' },
        viz: { type: 'spark', percent: pct, direction, word: 'lighter' },
        source: 'insight_spend_comparison',
        // "Spending down" is one of the approved amount-bearing moments.
        ...(savedDollars >= 1
          ? { detailed: { vars: { amount: formatShareAmount(savedDollars) } } }
          : {}),
      });
    }
    case 'milestone': {
      const p = payload as StatPayloadLike;
      const value = typeof p.value === 'number' ? String(round(p.value)) : (p.value ?? '');
      if (!value) return null;
      const label = (p.label ?? 'moments of noticing').toLowerCase();
      return buildMoment(templates, {
        templateId: 'insight_milestone',
        kind: 'milestone',
        vars: { value, label },
        stat: { value, unit: p.unit },
        viz: { type: 'stat' },
        source: 'insight_milestone',
      });
    }
    case 'community-rank': {
      const p = payload as RankPayloadLike;
      const pct = round(p.percent ?? 0);
      if (pct < 1 || pct > 50) return null;
      return buildMoment(templates, {
        templateId: 'insight_rank',
        kind: 'rank',
        vars: { percent: pct },
        stat: { value: String(pct), unit: '%' },
        viz: { type: 'rank', percent: pct },
        source: 'insight_rank',
      });
    }
    default:
      return null;
  }
}

// ── Goals ───────────────────────────────────────────────────────────────────

const GOAL_TEMPLATE_IDS: Partial<Record<UserGoal['type'], ShareCardTemplateId>> = {
  category_spend: 'goal_category_spend',
  total_spend: 'goal_total_spend',
  total_debt: 'goal_total_debt',
  specific_debt_payoff: 'goal_specific_debt_payoff',
  no_new_debt: 'goal_no_new_debt',
  regret_ratio: 'goal_regret_ratio',
  app_sessions: 'goal_app_sessions',
  emergency_fund: 'goal_emergency_fund',
  monthly_savings: 'goal_monthly_savings',
  life_event: 'goal_life_event',
};

/** Dollar-denominated goal types → how the detailed variant frames the figure.
 *  'baseline-minus-current' = spend/debt reduction; 'current-minus-baseline' =
 *  savings growth. Goal types absent here (no_new_debt, regret_ratio,
 *  app_sessions) have no sensible dollar figure and stay private-only. */
const GOAL_AMOUNTS: Partial<
  Record<UserGoal['type'], { direction: 'baseline-minus-current' | 'current-minus-baseline'; unit: string }>
> = {
  category_spend: { direction: 'baseline-minus-current', unit: 'under' },
  total_spend: { direction: 'baseline-minus-current', unit: 'under' },
  total_debt: { direction: 'baseline-minus-current', unit: 'down' },
  specific_debt_payoff: { direction: 'baseline-minus-current', unit: 'down' },
  emergency_fund: { direction: 'current-minus-baseline', unit: 'saved' },
  monthly_savings: { direction: 'current-minus-baseline', unit: 'saved' },
  life_event: { direction: 'current-minus-baseline', unit: 'saved' },
};

/** Share moment for an achieved goal. Type-based copy only — goal titles are
 *  user-authored and may name accounts. Only success is shareable. */
export function momentFromGoal(
  goal: UserGoal,
  templates: ShareCardTemplates = DEFAULT_SHARE_CARD_TEMPLATES
): ShareMoment | null {
  const templateId = GOAL_TEMPLATE_IDS[goal.type];
  if (!templateId) return null;

  const amountSpec = GOAL_AMOUNTS[goal.type];
  const baseline = goal.baselineValue ?? 0;
  const current = goal.currentValue ?? 0;
  const amount =
    amountSpec?.direction === 'current-minus-baseline' ? current - baseline : baseline - current;
  const detailed: DetailedArgs | undefined =
    amountSpec && amount >= 1
      ? {
          vars: { amount: formatShareAmount(amount) },
          stat: { value: formatShareAmount(amount), unit: amountSpec.unit },
          viz: { type: 'stat' },
        }
      : undefined;

  return buildMoment(templates, {
    templateId,
    kind: 'goal',
    vars: {},
    stat: { value: '1', unit: 'goal met' },
    viz: { type: 'none' },
    source: 'goal_celebration',
    ...(detailed ? { detailed } : {}),
  });
}

// ── Pause ───────────────────────────────────────────────────────────────────

export interface PauseWinCounts {
  /** Shopping-break sessions completed (Quiet Days) */
  sessionsCompleted?: number;
  /** Items let go from the pre-purchase pause vault */
  itemsDismissed?: number;
  /** Total $ left unspent across dismissed items — surfaces ONLY in the
   *  explicit detailed mode, never in the default private card. */
  amountSaved?: number;
}

/** Share moment for pause wins — counts by default; the dismissed-items total
 *  is available behind the explicit detailed toggle. */
export function momentFromPause(
  counts: PauseWinCounts,
  templates: ShareCardTemplates = DEFAULT_SHARE_CARD_TEMPLATES
): ShareMoment | null {
  const sessions = counts.sessionsCompleted ?? 0;
  const dismissed = counts.itemsDismissed ?? 0;
  if (sessions >= 1) {
    const noun = sessions === 1 ? 'break' : 'breaks';
    return buildMoment(templates, {
      templateId: 'pause_breaks',
      kind: 'pause',
      vars: { count: sessions, noun },
      stat: { value: String(sessions), unit: noun },
      viz: sessions >= 2 ? { type: 'streak', count: sessions } : { type: 'stat' },
      source: 'pause_breaks',
    });
  }
  if (dismissed >= 1) {
    const noun = dismissed === 1 ? 'thing' : 'things';
    const amountSaved = counts.amountSaved ?? 0;
    return buildMoment(templates, {
      templateId: 'pause_dismissed',
      kind: 'pause',
      vars: { count: dismissed, noun },
      stat: { value: String(dismissed), unit: 'let go' },
      viz: { type: 'stat' },
      source: 'pause_dismissed',
      // "Items let go" is one of the approved amount-bearing moments.
      ...(amountSaved >= 1
        ? {
            detailed: {
              vars: { amount: formatShareAmount(amountSaved) },
              stat: { value: formatShareAmount(amountSaved), unit: 'unspent' },
            },
          }
        : {}),
    });
  }
  return null;
}
